import type { ReactNode } from "react";
import { Floaties, Sparkles } from "./fx";
import { HeroScene, type ScenePalette, type SceneKind } from "./HeroScene";

/** the look of each page: sky, what stands on the horizon and which 3D things float around */
export const TONES = {
  shop: { palette: "rose", scene: "market", things: ["coin", "gem", "cube"] },
  rank: { palette: "night", scene: "city", things: ["star", "coin", "star"] },
  goals: { palette: "mint", scene: "peaks", things: ["star", "gem", "coin"] },
  klass: { palette: "ocean", scene: "school", things: ["cube", "star", "coin"] },
  profile: { palette: "dusk", scene: "castles", things: ["star", "coin", "gem"] },
  teacher: { palette: "day", scene: "school", things: ["cube", "star"] },
  games: { palette: "day", scene: "castles", things: ["star", "cube", "coin"] },
  help: { palette: "day", scene: "castles", things: ["cube", "star"] },
  friends: { palette: "ocean", scene: "castles", things: ["star", "coin", "cube"] },
} as const satisfies Record<string, { palette: ScenePalette; scene: SceneKind; things: readonly ("coin" | "cube" | "star" | "gem")[] }>;
export type Tone = keyof typeof TONES;

/**
 * The header of a page: the same scene language as the front page (drawn sky, sparkles, 3D things that float and spin), a big glossy icon
 * badge, the title in the game font and a line of explanation. `children` go on the right as little stat bubbles (see `.hbubble`).
 */
export function PageHero({ tone, title, sub, icon, children, id = "ph-h", compact }: { tone: Tone; title: string; sub?: ReactNode; icon?: ReactNode; children?: ReactNode; id?: string; compact?: boolean }) {
  const t = TONES[tone];
  return (
    <section className={`page-hero tone-${tone}${compact ? " compact" : ""}`} aria-labelledby={id} data-spot>
      <HeroScene palette={t.palette} scene={t.scene} />
      <Sparkles n={compact ? 12 : 18} />
      <Floaties kinds={[...t.things]} n={compact ? 4 : 6} seed={tone.length} />
      <div className="ph-inner">
        {icon && <span className="ph-badge" aria-hidden="true">{icon}</span>}
        <div className="ph-text">
          <h1 id={id} className="title-game ph-title">{title}</h1>
          {sub && <p className="ph-sub">{sub}</p>}
        </div>
        {children && <div className="ph-side">{children}</div>}
      </div>
    </section>
  );
}

/** a stat bubble for the right side of a page hero */
export function HeroBubble({ icon, tone = "blue", value, label }: { icon: ReactNode; tone?: "gold" | "blue" | "pink" | "green" | "violet"; value: ReactNode; label: string }) {
  return (
    <div className="hbubble">
      <span className={`hb-ico ${tone}`}>{icon}</span>
      <span><b>{value}</b><small>{label}</small></span>
    </div>
  );
}
